import { createStore } from 'zustand/vanilla';

const createInvoiceStore = (supabaseClient, getUserId) => {
  if (!supabaseClient) {
    throw new Error('Supabase client is required to initialize the invoice store');
  }

  if (!getUserId || typeof getUserId !== 'function') {
    throw new Error('getUserId function is required to initialize the invoice store');
  }


  try {
    const store = createStore((set, get) => ({
      invoices: [],
      filteredInvoices: [],
      isLoading: false,
      error: null,
      isConnected: false,
      filters: {
        dateFrom: null,
        dateTo: null,
        amountMin: null,
        amountMax: null,
        searchTerm: '',
      },
      sortBy: 'date-desc',
      page: 1,
      pageSize: 50,
      hasMore: true,
      totalCount: 0,
      monthlySummary: [], // Array of { key: '2026-05', count: 10, totalAmount: 50000, isLoaded: false, isLoading: false, invoices: [] }

      setConnectionStatus: (status) => set({ isConnected: status }),

      fetchMonthlySummary: async () => {
        const { filters, sortBy } = get();
        
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          let allData = [];
          let currentCount = 0;
          let totalCount = 0;
          const pageSize = 1000;
          
          while (true) {
            let query = supabaseClient
              .from('invoices')
              .select('id, date, total', { count: 'exact' })
              .eq('user_id', userId);

            // Server-side filtering
            if (filters.dateFrom) query = query.gte('date', filters.dateFrom);
            if (filters.dateTo) query = query.lte('date', filters.dateTo);
            if (filters.amountMin) query = query.gte('total', parseFloat(filters.amountMin));
            if (filters.amountMax) query = query.lte('total', parseFloat(filters.amountMax));
            if (filters.searchTerm) {
              const term = `%${filters.searchTerm}%`;
              query = query.or(`invoice_number.ilike.${term},billing_name.ilike.${term},billing_city.ilike.${term},estado.ilike.${term},num_identificacion.ilike.${term}`);
            }

            query = query.range(currentCount, currentCount + pageSize - 1);
            const { data, error, count } = await query;
            if (error) throw error;
            
            if (count !== null && totalCount === 0) totalCount = count;
            
            if (data && data.length > 0) {
              allData = [...allData, ...data];
              currentCount += data.length;
            }
            
            if (!data || data.length < pageSize) {
              break;
            }
          }

          // Group by month
          const summaryMap = {};
          allData.forEach(inv => {
            if (!inv.date) return;
            // The DB returns YYYY-MM-DD
            const key = inv.date.substring(0, 7); // Extracts 'YYYY-MM' directly
            if (!summaryMap[key]) {
              summaryMap[key] = { key, count: 0, totalAmount: 0, isLoaded: false, isLoading: false, invoices: [] };
            }
            summaryMap[key].count++;
            summaryMap[key].totalAmount += parseFloat(inv.total || 0);
          });

          let summaryArray = Object.values(summaryMap);
          // Sort groups by key descending (newest month first)
          summaryArray.sort((a, b) => b.key.localeCompare(a.key));

          set({
            monthlySummary: summaryArray,
            totalCount: totalCount || 0,
            isLoading: false,
            isConnected: true,
          });
        } catch (error) {
          console.error('Error fetching monthly summary:', error.message);
          set({
            error: 'Failed to load invoices summary. Please try again later.',
            isLoading: false,
            isConnected: false,
          });
        }
      },

      fetchInvoicesForMonth: async (monthKey) => {
        const { filters, sortBy, monthlySummary } = get();
        
        // Find group and set loading
        const groupIndex = monthlySummary.findIndex(g => g.key === monthKey);
        if (groupIndex === -1) return;
        
        const updatedSummary = [...monthlySummary];
        updatedSummary[groupIndex] = { ...updatedSummary[groupIndex], isLoading: true };
        set({ monthlySummary: updatedSummary, error: null });

        try {
          const userId = getUserId();
          if (!userId) throw new Error('User not authenticated');

          const [yearStr, monthStr] = monthKey.split('-');
          const lastDayNum = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10), 0).getDate();
          const lastDayStr = `${monthKey}-${String(lastDayNum).padStart(2, '0')}`;

          let allData = [];
          let currentCount = 0;
          const pageSize = 1000;

          while (true) {
            let query = supabaseClient
              .from('invoices')
              .select('*, invoice_items (*), accounting_entries(debit, credit)')
              .eq('user_id', userId)
              // Filter by month
              .gte('date', `${monthKey}-01`)
              .lte('date', lastDayStr);

            // Server-side filtering
            if (filters.dateFrom) query = query.gte('date', filters.dateFrom);
            if (filters.dateTo) query = query.lte('date', filters.dateTo);
            if (filters.amountMin) query = query.gte('total', parseFloat(filters.amountMin));
            if (filters.amountMax) query = query.lte('total', parseFloat(filters.amountMax));
            if (filters.searchTerm) {
              const term = `%${filters.searchTerm}%`;
              query = query.or(`invoice_number.ilike.${term},billing_name.ilike.${term},billing_city.ilike.${term},estado.ilike.${term},num_identificacion.ilike.${term}`);
            }

            // Server-side sorting
            switch (sortBy) {
              case 'date-asc': query = query.order('date', { ascending: true, nullsFirst: false }); break;
              case 'amount-desc': query = query.order('total', { ascending: false, nullsFirst: false }); break;
              case 'amount-asc': query = query.order('total', { ascending: true, nullsFirst: false }); break;
              case 'date-desc':
              default: query = query.order('date', { ascending: false, nullsFirst: false }); break;
            }

            query = query.range(currentCount, currentCount + pageSize - 1);
            const { data, error } = await query;
            if (error) throw error;
            
            if (data && data.length > 0) {
              allData = [...allData, ...data];
              currentCount += data.length;
            }
            
            if (!data || data.length < pageSize) {
              break;
            }
          }

          const finalSummary = [...get().monthlySummary];
          const finalGroupIndex = finalSummary.findIndex(g => g.key === monthKey);
          if (finalGroupIndex !== -1) {
             finalSummary[finalGroupIndex] = {
               ...finalSummary[finalGroupIndex],
               isLoading: false,
               isLoaded: true,
               invoices: allData || []
             };
             set({ monthlySummary: finalSummary });
          }

        } catch (error) {
          console.error(`Error fetching invoices for month ${monthKey}:`, error.message);
          const finalSummary = [...get().monthlySummary];
          const finalGroupIndex = finalSummary.findIndex(g => g.key === monthKey);
          if (finalGroupIndex !== -1) {
             finalSummary[finalGroupIndex] = {
               ...finalSummary[finalGroupIndex],
               isLoading: false,
               isLoaded: false
             };
             set({ monthlySummary: finalSummary, error: 'Failed to load month data' });
          }
        }
      },

      updateInvoiceStatus: async (invoiceId, status) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { error } = await supabaseClient
            .from('invoices')
            .update({ estado: status })
            .eq('id', invoiceId)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            monthlySummary: state.monthlySummary.map(group => {
              if (!group.invoices) return group;
              return {
                ...group,
                invoices: group.invoices.map(inv =>
                  inv.id === invoiceId ? { ...inv, estado: status } : inv
                )
              };
            })
          }));
          return { success: true };
        } catch (error) {
          console.error('Error updating invoice status:', error.message);
          set({
            error: 'Failed to update invoice status. Please try again later.',
            isLoading: false,
          });
          return { success: false, error };
        }
      },

      updateInvoiceType: async (invoiceId, invoiceType) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { error } = await supabaseClient
            .from('invoices')
            .update({ invoice_type: invoiceType })
            .eq('id', invoiceId)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            monthlySummary: state.monthlySummary.map(group => {
              if (!group.invoices) return group;
              return {
                ...group,
                invoices: group.invoices.map(inv =>
                  inv.id === invoiceId ? { ...inv, invoice_type: invoiceType } : inv
                )
              };
            })
          }));
          return { success: true };
        } catch (error) {
          console.error('Error updating invoice type:', error.message);
          set({
            error: 'Failed to update invoice type. Please try again later.',
            isLoading: false,
          });
          return { success: false, error };
        }
      },

      updateInvoiceCustomDocType: async (invoiceId, customDocType) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { error } = await supabaseClient
            .from('invoices')
            .update({ custom_doc_type: customDocType })
            .eq('id', invoiceId)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            monthlySummary: state.monthlySummary.map(group => {
              if (!group.invoices) return group;
              return {
                ...group,
                invoices: group.invoices.map(inv =>
                  inv.id === invoiceId ? { ...inv, custom_doc_type: customDocType } : inv
                )
              };
            })
          }));
          return { success: true };
        } catch (error) {
          console.error('Error updating custom doc type:', error.message);
          set({
            error: 'Failed to update custom doc type. Please try again later.',
            isLoading: false,
          });
          return { success: false, error };
        }
      },

      updateInvoiceNumber: async (invoiceId, invoiceNumber) => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { error } = await supabaseClient
            .from('invoices')
            .update({ invoice_number: invoiceNumber })
            .eq('id', invoiceId)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            monthlySummary: state.monthlySummary.map(group => {
              if (!group.invoices) return group;
              return {
                ...group,
                invoices: group.invoices.map(inv =>
                  inv.id === invoiceId ? { ...inv, invoice_number: invoiceNumber } : inv
                )
              };
            }),
            isLoading: false,
          }));
          return { success: true };
        } catch (error) {
          console.error('Error updating invoice number:', error.message);
          set({
            error: 'Failed to update invoice number. Please try again later.',
            isLoading: false,
          });
          return { success: false, error };
        }
      },

      addInvoice: async (invoiceId) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('invoices')
            .select('*, invoice_items (*), accounting_entries(debit, credit)')
            .eq('id', invoiceId)
            .eq('user_id', userId)
            .single();

          if (error) throw error;

          const monthKey = data.date ? data.date.substring(0, 7) : null;
          if (!monthKey) return;

          set((state) => {
            const newSummary = [...state.monthlySummary];
            const groupIndex = newSummary.findIndex(g => g.key === monthKey);
            
            if (groupIndex !== -1) {
              const group = newSummary[groupIndex];
              // Avoid duplicates
              const exists = group.invoices && group.invoices.some(inv => inv.id === invoiceId);
              if (!exists) {
                newSummary[groupIndex] = {
                  ...group,
                  count: group.count + 1,
                  total: group.total + (data.total || 0),
                  invoices: group.invoices ? [data, ...group.invoices] : undefined
                };
              }
            } else {
              // If the month doesn't exist yet, we just trigger a full fetch of the summary
              // to rebuild the list
              setTimeout(() => get().fetchMonthlySummary(), 100);
            }
            return { monthlySummary: newSummary };
          });
        } catch (error) {
          console.error('Error adding new invoice:', error.message);
        }
      },

      updateInvoice: async (invoiceId) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('invoices')
            .select('*, invoice_items (*), accounting_entries(debit, credit)')
            .eq('id', invoiceId)
            .eq('user_id', userId)
            .single();

          if (error) throw error;

          set((state) => ({
            monthlySummary: state.monthlySummary.map(group => {
              if (!group.invoices) return group;
              return {
                ...group,
                invoices: group.invoices.map(inv =>
                  inv.id === invoiceId ? data : inv
                )
              };
            })
          }));
        } catch (error) {
          console.error('Error updating invoice:', error.message);
        }
      },

      deleteInvoice: (invoiceId) => {
        set((state) => ({
          monthlySummary: state.monthlySummary.map(group => {
            if (!group.invoices) return group;
            const updatedInvoices = group.invoices.filter(inv => inv.id !== invoiceId);
            if (updatedInvoices.length !== group.invoices.length) {
              // Adjust count and total optionally, or just leave it since it's UI realtime
              return {
                ...group,
                count: group.count - 1,
                invoices: updatedInvoices
              };
            }
            return group;
          })
        }));
      },

      setFilters: (newFilters) => {
        set((state) => ({ filters: { ...state.filters, ...newFilters }, page: 1, hasMore: true }));
        get().fetchMonthlySummary();
      },
      resetFilters: () => {
        set({
          filters: { dateFrom: null, dateTo: null, amountMin: null, amountMax: null, searchTerm: '' },
          page: 1,
          hasMore: true
        });
        get().fetchMonthlySummary();
      },
      setSort: (sortBy) => {
        set({ sortBy, page: 1, hasMore: true });
        get().fetchMonthlySummary();
      },

      setupRealtimeSubscription: () => {
        const userId = getUserId();
        if (!userId) {
          console.error('Cannot setup realtime: user not authenticated');
          return;
        }

        // Remove existing subscriptions
        const channels = supabaseClient.getChannels();
        channels.forEach(channel => {
          supabaseClient.removeChannel(channel);
        });

        // Create new channel for invoices
        const channel = supabaseClient
          .channel('invoices-realtime')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'invoices',
              filter: `user_id=eq.${userId}`
            },
            (payload) => {
              console.log('New invoice received:', payload);
              get().addInvoice(payload.new.id);
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'invoices',
              filter: `user_id=eq.${userId}`
            },
            (payload) => {
              console.log('Invoice updated:', payload);
              get().updateInvoice(payload.new.id);
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'DELETE',
              schema: 'public',
              table: 'invoices',
              filter: `user_id=eq.${userId}`
            },
            (payload) => {
              console.log('Invoice deleted:', payload);
              get().deleteInvoice(payload.old.id);
            }
          )
          .subscribe((status) => {
            console.log('Realtime subscription status:', status);
            set({ isConnected: status === 'SUBSCRIBED' });
          });

        return channel;
      },

      applyFiltersAndSort: () => {
        // Obsolete local filtering logic. We just sync the arrays.
        // Server handles actual filtering and sorting in fetchInvoices.
        set({ filteredInvoices: get().invoices });
      },
    }));

    return store;
  } catch (error) {
    console.error('Error creating invoice store:', error);
    throw error;
  }
};

// Export predeterminado: función que crea el store
export default createInvoiceStore;